import React, { useState, useEffect } from 'react';
import { employeeApi } from '../../api/employeeApi';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, ClockIcon, DocumentArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../../context/AuthContext';

const BulkAttendance = () => {
  const { activeBusiness } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [attendanceState, setAttendanceState] = useState({}); // { empId: 'present' | 'absent' | 'half-day' }
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, [selectedDate]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await employeeApi.getEmployees();
      const emps = res.data.data;
      setEmployees(emps);
      
      // Fetch existing attendance for the selected date to pre-fill
      let existingAttendanceMap = {};
      try {
        const attendanceRes = await employeeApi.getAllAttendance({ 
          startDate: selectedDate, 
          endDate: selectedDate 
        });
        const records = attendanceRes.data.data;
        records.forEach(record => {
          if (record.employeeId && record.employeeId._id) {
            existingAttendanceMap[record.employeeId._id.toString()] = record.status;
          } else if (record.employeeId) {
            // in case populate didn't work as expected
            existingAttendanceMap[record.employeeId.toString()] = record.status;
          }
        });
      } catch (attError) {
        console.error("Failed to fetch existing attendance", attError);
      }

      const initialState = {};
      emps.forEach(emp => {
        const empId = emp._id.toString();
        initialState[empId] = existingAttendanceMap[empId] || 'upcoming';
      });
      
      setAttendanceState(initialState);
      
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (empId, status) => {
    setAttendanceState(prev => ({ ...prev, [empId]: status }));
  };

  const markAllStatus = (status) => {
    const newState = {};
    employees.forEach(emp => {
      newState[emp._id] = status;
    });
    setAttendanceState(newState);
  };

  const handleSaveAttendance = async () => {
    try {
      setSaving(true);
      const records = Object.keys(attendanceState).map(empId => ({
        employeeId: empId,
        status: attendanceState[empId]
      }));

      await employeeApi.bulkMarkAttendance({
        date: selectedDate,
        records
      });

      toast.success(`Attendance saved for ${records.length} employees!`);
    } catch (error) {
      toast.error('Failed to save bulk attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!reportStart || !reportEnd) {
      toast.error('Please select start and end dates');
      return;
    }

    try {
      setGeneratingReport(true);
      const res = await employeeApi.getAllAttendance({ startDate: reportStart, endDate: reportEnd });
      const records = res.data.data;

      if (records.length === 0) {
        toast.error('No attendance records found for this date range');
        return;
      }

      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(18);
      doc.text('Attendance Report', 14, 22);
      doc.setFontSize(11);
      doc.text(`Date Range: ${new Date(reportStart).toLocaleDateString()} to ${new Date(reportEnd).toLocaleDateString()}`, 14, 30);

      // Table mapping
      const tableColumn = ["Date", "Employee ID", "Name", "Role", "Status"];
      const tableRows = [];

      records.forEach(record => {
        const empName = record.employeeId?.name || 'Unknown';
        const empRole = record.employeeId?.role || 'Unknown';
        const empIdStr = record.employeeId?._id ? `EMP-${record.employeeId._id.toString().substring(18, 24).toUpperCase()}` : 'N/A';
        const dateStr = new Date(record.date).toLocaleDateString();
        const status = record.status.toUpperCase();
        
        tableRows.push([
          dateStr,
          empIdStr,
          empName,
          empRole,
          status
        ]);
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [220, 52, 59] } // #dc343b red accent
      });

      // --- Summary Section ---
      const summaryMap = {};
      
      records.forEach(record => {
        if (!record.employeeId) return;
        const eId = record.employeeId._id.toString();
        if (!summaryMap[eId]) {
          summaryMap[eId] = {
            empIdStr: `EMP-${eId.substring(18, 24).toUpperCase()}`,
            name: record.employeeId.name,
            salaryAmount: record.employeeId.salaryAmount || 0,
            salaryType: record.employeeId.salaryType || 'monthly',
            present: 0,
            absent: 0,
            halfDay: 0
          };
        }
        
        if (record.status === 'present') summaryMap[eId].present++;
        if (record.status === 'absent') summaryMap[eId].absent++;
        if (record.status === 'half-day') summaryMap[eId].halfDay++;
      });

      const summaryRows = Object.values(summaryMap).map(s => {
        const allowedLeaves = activeBusiness?.settings?.allowedPaidLeavesPerMonth || 2;
        let perDay = s.salaryType === 'monthly' ? s.salaryAmount / 30 : s.salaryAmount;
        
        // Paid leave logic: up to 'allowedLeaves' are ignored from absents
        let effectiveAbsent = Math.max(0, s.absent - allowedLeaves);
        let paidLeavesUsed = Math.min(s.absent, allowedLeaves);
        
        // Earned = Present + Half-day/2 + PaidLeavesUsed
        let earned = ((s.present + paidLeavesUsed) * perDay) + (s.halfDay * perDay / 2);
        
        return [
          s.empIdStr, 
          s.name, 
          s.present.toString(), 
          `${s.absent} (Paid: ${paidLeavesUsed})`, 
          s.halfDay.toString(),
          `Rs. ${Math.round(earned)}`
        ];
      });

      // Get Y position after the first table
      const finalY = doc.lastAutoTable.finalY + 15;
      
      doc.setFontSize(14);
      doc.text('Employee Attendance Summary', 14, finalY);
      
      doc.autoTable({
        head: [['Employee ID', 'Name', 'Total Present', 'Total Absent', 'Total Half-Days', 'Earned Salary']],
        body: summaryRows,
        startY: finalY + 5,
        theme: 'grid',
        styles: { fontSize: 9, halign: 'center' },
        columnStyles: { 0: { halign: 'left' }, 1: { halign: 'left' }, 5: { halign: 'right', fontStyle: 'bold', textColor: [220, 52, 59] } },
        headStyles: { fillColor: [44, 62, 80] } // Dark blue header for summary
      });

      doc.save(`Attendance_Report_${reportStart}_to_${reportEnd}.pdf`);
      toast.success('Report downloaded successfully!');
      setShowReportModal(false);
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <div className="w-full pb-20 max-w-7xl mx-auto space-y-6 pt-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Daily Register</h1>
          <p className="text-[14px] text-gray-500">Mark attendance for all employees at once</p>
        </div>
        <div className="flex items-center gap-3">
          <input 
            type="date" 
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 border-2 border-gray-100 rounded-xl font-bold text-sm focus:border-[#dc343b] focus:ring-0"
          />
          <button 
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 bg-white text-gray-700 border-2 border-gray-200 px-4 py-2 rounded-xl font-bold text-[13px] hover:bg-gray-50 transition-colors shadow-sm"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            Report
          </button>
          <button 
            onClick={handleSaveAttendance}
            disabled={saving}
            className="bg-[#dc343b] text-white px-6 py-2.5 rounded-xl font-bold text-[13px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20 disabled:opacity-70"
          >
            {saving ? 'Saving...' : 'Save Register'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 dark:border-gray-700 overflow-hidden">
        
        {/* Quick Actions Bar */}
        <div className="p-4 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-2">
          <span className="text-sm font-bold text-gray-500 mr-4 self-center">Quick Select:</span>
          <button onClick={() => markAllStatus('upcoming')} className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-bold border border-gray-300 hover:bg-gray-300 transition-colors">All Upcoming</button>
          <button onClick={() => markAllStatus('present')} className="px-4 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-bold border border-green-200 hover:bg-green-200 transition-colors">All Present</button>
          <button onClick={() => markAllStatus('absent')} className="px-4 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-bold border border-red-200 hover:bg-red-200 transition-colors">All Absent</button>
          <button onClick={() => markAllStatus('half-day')} className="px-4 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg text-xs font-bold border border-yellow-200 hover:bg-yellow-200 transition-colors">All Half-Day</button>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc343b]"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-400 uppercase bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-4 font-bold tracking-wider">Employee</th>
                  <th scope="col" className="px-6 py-4 font-bold tracking-wider">Role</th>
                  <th scope="col" className="px-6 py-4 font-bold tracking-wider text-center">Attendance Status</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee._id} className="bg-white dark:bg-gray-800 border-b border-gray-50 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                          {employee.profilePicture ? (
                            <img src={employee.profilePicture} alt={employee.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-gray-400 font-bold text-lg">{employee.name.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 dark:text-white text-[15px]">{employee.name}</p>
                          <p className="text-[11px] font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 inline-block mt-0.5">
                            #EMP-{employee._id.substring(18, 24).toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-600">
                      {employee.role}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleStatusChange(employee._id, 'upcoming')}
                          className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold transition-all border-2 ${
                            attendanceState[employee._id] === 'upcoming'
                              ? 'bg-gray-100 text-gray-700 border-gray-400 shadow-sm'
                              : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'
                          }`}
                        >
                           Upcoming
                        </button>
                        
                        <button
                          onClick={() => handleStatusChange(employee._id, 'present')}
                          className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold transition-all border-2 ${
                            attendanceState[employee._id] === 'present'
                              ? 'bg-green-50 text-green-700 border-green-500 shadow-sm'
                              : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'
                          }`}
                        >
                          <CheckCircleIcon className="w-4 h-4" /> Present
                        </button>
                        
                        <button
                          onClick={() => handleStatusChange(employee._id, 'half-day')}
                          className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold transition-all border-2 ${
                            attendanceState[employee._id] === 'half-day'
                              ? 'bg-yellow-50 text-yellow-700 border-yellow-500 shadow-sm'
                              : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'
                          }`}
                        >
                          <ClockIcon className="w-4 h-4" /> Half
                        </button>
                        
                        <button
                          onClick={() => handleStatusChange(employee._id, 'absent')}
                          className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold transition-all border-2 ${
                            attendanceState[employee._id] === 'absent'
                              ? 'bg-red-50 text-red-700 border-red-500 shadow-sm'
                              : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'
                          }`}
                        >
                          <XCircleIcon className="w-4 h-4" /> Absent
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {employees.length === 0 && !loading && (
              <div className="p-8 text-center text-gray-400 font-bold">
                No employees found. Add staff first.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm px-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-gray-100 dark:border-gray-700">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <DocumentArrowDownIcon className="w-6 h-6 text-[#dc343b]" />
                  Download Report
                </h3>
                <button onClick={() => setShowReportModal(false)} className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                  <input 
                    type="date" 
                    value={reportStart}
                    onChange={(e) => setReportStart(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl font-bold text-sm focus:border-[#dc343b] focus:ring-0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                  <input 
                    type="date" 
                    value={reportEnd}
                    onChange={(e) => setReportEnd(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl font-bold text-sm focus:border-[#dc343b] focus:ring-0"
                  />
                </div>
              </div>

              <div className="mt-8 flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateReport}
                  disabled={generatingReport}
                  className="flex-1 px-4 py-3 bg-[#dc343b] text-white rounded-xl font-bold text-sm hover:bg-[#b91c1c] transition-colors shadow-lg shadow-red-500/30 disabled:opacity-70 flex justify-center items-center gap-2"
                >
                  {generatingReport ? 'Generating...' : (
                    <>
                      <DocumentArrowDownIcon className="w-5 h-5" /> Download PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BulkAttendance;
